import React from "react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { InlineEditPopover } from "../InlineEditPopover";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Exercise } from "@/schemas/workout";
import { Button } from "../ui/button";
import { Copy, Trash2 } from "lucide-react";

// removed ExerciseNamePopover import; using InlineEditPopover for name editing

interface WorkoutTableProps {
  exercises: Exercise[];
  editingField: { exerciseId: string; field: string } | null;
  startEditing: (exerciseId: string, field: string) => void;
  stopEditing: () => void;
  handleExerciseChange: (updatedExercise: Exercise) => void;
  handleExerciseDuplicate: (id: string) => void;
  handleExerciseDelete: (id: string) => void;
  handleExerciseTypeChange: (exerciseId: string, type: Exercise["structure"]["type"]) => void;
  onReorderExercises: (newExercises: Exercise[]) => void;
}

export const WorkoutTable: React.FC<WorkoutTableProps> = ({
  exercises,
  editingField,
  startEditing,
  stopEditing,
  handleExerciseChange,
  handleExerciseDuplicate,
  handleExerciseDelete,
  handleExerciseTypeChange,
  onReorderExercises,
}) => {
  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;
    const reordered = Array.from(exercises);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    onReorderExercises(reordered);
  }

  return (
    <div className="bg-card rounded-xl border shadow-lg overflow-hidden">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <TableHeader className="bg-muted dark:bg-gray-800">
            <TableRow>
              <TableHead className="w-12 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">#</TableHead>
              <TableHead className="w-1/3 px-4 py-2 whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Exercise</TableHead>
              <TableHead className="w-24 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Type</TableHead>
              <TableHead className="w-20 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Sets</TableHead>
              <TableHead className="w-20 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Reps</TableHead>
              <TableHead className="w-32 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Duration (sec)</TableHead>
              <TableHead className="w-32 px-4 py-2 text-center whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Rest (sec)</TableHead>
              <TableHead className="w-24 px-4 py-2 text-right whitespace-nowrap font-semibold tracking-wide text-xs uppercase text-gray-500 dark:text-gray-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <Droppable droppableId="exercises-droppable">
            {(provided) => (
              <TableBody
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {exercises.map((exercise, index) => (
                  <Draggable key={exercise.id} draggableId={exercise.id} index={index}>
                    {(provided, snapshot) => (
                      <tr
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="hover:bg-muted/50"
                        style={{
                          ...provided.draggableProps.style,
                          background: snapshot.isDragging ? '#f3f4f6' : undefined,
                        }}
                      >
                        <TableCell className="w-12 px-4 py-2 text-center whitespace-nowrap font-mono text-muted-foreground">
                          {index + 1}
                        </TableCell>
                        <TableCell className="w-1/3 px-4 py-2 whitespace-nowrap">
                          <InlineEditPopover
                            open={editingField?.exerciseId === exercise.id && editingField?.field === "name"}
                            onOpenChange={open => open ? startEditing(exercise.id, "name") : stopEditing()}
                            value={exercise.name}
                            onSubmit={val => { handleExerciseChange({ ...exercise, name: val }); stopEditing(); }}
                            onCancel={stopEditing}
                          >
                            <span className="hover:underline cursor-pointer">{exercise.name}</span>
                          </InlineEditPopover>
                        </TableCell>
                        <TableCell
                          key="type"
                          className={`w-24 px-4 py-2 text-center whitespace-nowrap transition-colors duration-150 ${
                            editingField?.exerciseId === exercise.id && editingField?.field === "type"
                              ? 'ring-2 ring-primary bg-primary/10 dark:bg-primary/20'
                              : 'hover:bg-muted/60 dark:hover:bg-muted/40 cursor-pointer group'
                          }`}
                        >
                          <InlineEditPopover
                            open={editingField?.exerciseId === exercise.id && editingField?.field === "type"}
                            onOpenChange={open => {
                              if (open) {
                                startEditing(exercise.id, "type");
                              } else {
                                stopEditing();
                              }
                            }}
                            value={exercise.structure.type}
                            onSubmit={val => handleExerciseTypeChange(exercise.id, val as Exercise["structure"]["type"])}
                            onCancel={stopEditing}
                            renderContent={({ inputRef }) => (
                              <select
                                ref={inputRef}
                                value={exercise.structure.type}
                                onChange={e => {
                                  handleExerciseTypeChange(exercise.id, e.target.value as Exercise["structure"]["type"]);
                                  stopEditing();
                                }}
                                onBlur={stopEditing}
                                autoFocus
                                className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary dark:bg-muted dark:text-white dark:border-gray-700"
                              >
                                <option value="sets">Sets</option>
                                <option value="timed">Timed</option>
                                <option value="cadence">Cadence</option>
                                <option value="interval">Interval</option>
                              </select>
                            )}
                          >
                            <span className="group-hover:underline group-hover:text-primary flex items-center gap-1">{exercise.structure.type}
                              <svg className="w-3 h-3 opacity-60 group-hover:opacity-100" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15.232 5.232l3.536 3.536M9 11l6 6M6 18v-3.414a2 2 0 01.586-1.414l9.293-9.293a2 2 0 112.828 2.828l-9.293 9.293A2 2 0 019.414 18H6z" /></svg>
                            </span>
                          </InlineEditPopover>
                        </TableCell>
                        <TableCell className="w-20 px-4 py-2 text-center whitespace-nowrap">
                          <InlineEditPopover
                            open={editingField?.exerciseId === exercise.id && editingField?.field === "sets"}
                            onOpenChange={open => open ? startEditing(exercise.id, "sets") : stopEditing()}
                            value={exercise.structure.sets}
                            type="number"
                            onSubmit={val => {
                              const struct = exercise.structure;
                              if (struct.type === "sets") {
                                handleExerciseChange({
                                  ...exercise,
                                  structure: { type: "sets", sets: Number(val), reps: struct.reps }
                                });
                              } else if (struct.type === "timed") {
                                handleExerciseChange({
                                  ...exercise,
                                  structure: { type: "timed", sets: Number(val), duration: struct.duration }
                                });
                              } else if (struct.type === "cadence") {
                                handleExerciseChange({
                                  ...exercise,
                                  structure: { type: "cadence", sets: Number(val), reps: struct.reps, tempo: struct.tempo }
                                });
                              }
                              stopEditing();
                            }}
                            onCancel={stopEditing}
                          >
                            <span className="hover:underline cursor-pointer">{exercise.structure.sets}</span>
                          </InlineEditPopover>
                        </TableCell>
                        <TableCell className="w-20 px-4 py-2 text-center whitespace-nowrap">
                          {exercise.structure.type === "sets" ? (
                            <InlineEditPopover
                              open={editingField?.exerciseId === exercise.id && editingField?.field === "reps"}
                              onOpenChange={open => open ? startEditing(exercise.id, "reps") : stopEditing()}
                              value={(exercise.structure as { type: "sets"; sets: number; reps: number }).reps}
                              type="number"
                              onSubmit={val => {
                                const struct = exercise.structure as { type: 'sets'; sets: number; reps: number };
                                handleExerciseChange({
                                  ...exercise,
                                  structure: { type: 'sets', sets: struct.sets, reps: Number(val) }
                                });
                                stopEditing();
                              }}
                              onCancel={stopEditing}
                            >
                              <span className="hover:underline cursor-pointer">
                                {(exercise.structure as { type: "sets"; sets: number; reps: number }).reps}
                              </span>
                            </InlineEditPopover>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="w-32 px-4 py-2 text-center whitespace-nowrap">
                          {exercise.structure.type === "timed" ? (
                            <InlineEditPopover
                              open={editingField?.exerciseId === exercise.id && editingField?.field === "duration"}
                              onOpenChange={open => open ? startEditing(exercise.id, "duration") : stopEditing()}
                              value={(exercise.structure as { type: "timed"; sets: number; duration: number }).duration}
                              type="number"
                              onSubmit={val => {
                                const { sets } = exercise.structure as { type: "timed"; sets: number; duration: number };
                                handleExerciseChange({
                                  ...exercise,
                                  structure: { type: "timed", sets, duration: Number(val) }
                                });
                                stopEditing();
                              }}
                              onCancel={stopEditing}
                            >
                              <span className="hover:underline cursor-pointer">
                                {(exercise.structure as { type: "timed"; sets: number; duration: number }).duration}
                              </span>
                            </InlineEditPopover>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="w-32 px-4 py-2 text-center whitespace-nowrap">
                          <InlineEditPopover
                            open={editingField?.exerciseId === exercise.id && editingField?.field === "rest_time"}
                            onOpenChange={open => open ? startEditing(exercise.id, "rest_time") : stopEditing()}
                            value={exercise.rest_time ?? 0}
                            type="number"
                            onSubmit={val => {
                              handleExerciseChange({ ...exercise, rest_time: Number(val) });
                              stopEditing();
                            }}
                            onCancel={stopEditing}
                          >
                            <span className="hover:underline cursor-pointer">{exercise.rest_time ?? "—"}</span>
                          </InlineEditPopover>
                        </TableCell>
                        <TableCell className="w-24 px-4 py-2 whitespace-nowrap">
                          <div className="flex gap-1 items-center justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleExerciseDuplicate(exercise.id)}
                            >
                              <Copy className="h-4 w-4" />
                              <span className="sr-only">Duplicate exercise</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleExerciseDelete(exercise.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete exercise</span>
                            </Button>
                          </div>
                        </TableCell>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </DragDropContext>
    </div>
  );
};
